/**
 * Cushion Configurator — GA4 Tracking
 * uscushion.com · 2026-07
 *
 * Standalone tracking script for ALL configurator shapes.
 * Monitors user behavior through the configuration funnel
 * via shared DOM elements and custom events.
 *
 * Load AFTER configurator JS and cart-drawer JS in block.liquid:
 *   {{ 'cushion-configurator-tracking.js' | asset_url | script_tag }}
 *
 * Events fired:
 *   cc_page_view       — configurator page loaded
 *   cc_dims_started    — first dimension input interaction
 *   cc_dims_complete   — all required dimensions filled
 *   cc_option_select   — any option card or dropdown changed
 *   cc_fabric_selected — fabric chosen
 *   cc_price_shown     — first time a real price appears
 *   cc_ready           — add-to-order button becomes enabled
 *   cc_add_click       — add-to-order button clicked
 *   cc_step_dwell      — user stares at a step >15s without acting
 *   cc_abandon         — user leaves page mid-configuration
 */
(function () {
  'use strict';

  function boot() {
    /* ─── Guard: only run on configurator pages ─── */
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    /* ─── Ensure gtag exists (Shopify Google channel loads gtag.js
           via content_for_header but doesn't expose the function) ─── */
    if (typeof window.gtag !== 'function') {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { dataLayer.push(arguments); };
    }

    var shapeType = wrap.getAttribute('data-formula-type') || 'bench';
    var pageStart = Date.now();

  /* ─── One-shot flags (fire each event at most once per page view) ─── */
  var fired = {};
  function once(eventName, params) {
    if (fired[eventName]) return;
    fired[eventName] = true;
    params = params || {};
    params.shape_type = shapeType;
    gtag('event', eventName, params);
  }

  /* ─── Helpers ─── */
  function getPrice() {
    var el = document.getElementById('cc-price-main');
    if (!el) return 0;
    var m = el.textContent.replace(/[^0-9.]/g, '');
    return m ? parseFloat(m) : 0;
  }

  function getElVal(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }

  function secondsOnPage() {
    return Math.round((Date.now() - pageStart) / 1000);
  }

  /* ─── 1. cc_page_view ─── */
  once('cc_page_view', {
    referrer: document.referrer || '(direct)',
    page_path: location.pathname
  });

  /* ─── 2. cc_dims_started — first focus/input on any dimension field ─── */
  var dimIds = [
    'cc-dim-l', 'cc-dim-w', 'cc-dim-t',
    'cc-back-l', 'cc-back-w', 'cc-back-t'
  ];
  dimIds.forEach(function (id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('focus', function () {
      once('cc_dims_started', { field: id });
    });
  });

  /* ─── 3. cc_dims_complete — all visible required dims have valid values ─── */
  var dimsCompleteFired = false;
  function checkDimsComplete() {
    if (dimsCompleteFired) return;
    var seatL = getElVal('cc-dim-l');
    var seatW = getElVal('cc-dim-w');
    var seatT = getElVal('cc-dim-t');
    if (!seatL || !seatW || !seatT) return;

    // RV shape also needs back dimensions
    if (shapeType === 'rv') {
      var backL = getElVal('cc-back-l');
      var backW = getElVal('cc-back-w');
      var backT = getElVal('cc-back-t');
      if (!backL || !backW || !backT) return;
      dimsCompleteFired = true;
      once('cc_dims_complete', {
        seat_l: seatL, seat_w: seatW, seat_t: seatT,
        back_l: backL, back_w: backW, back_t: backT
      });
      return;
    }

    dimsCompleteFired = true;
    once('cc_dims_complete', {
      length: seatL, width: seatW, thickness: seatT
    });
  }

  dimIds.forEach(function (id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('input', checkDimsComplete);
  });

  /* ─── 4. cc_option_select — card clicks and dropdown changes ─── */
  // Option cards (shape, piping, straps, zipper, antislip)
  wrap.addEventListener('click', function (e) {
    var card = e.target.closest('.cc-opt-card');
    if (!card) return;
    var group = card.getAttribute('data-group');
    var value = card.getAttribute('data-value');
    if (!group || !value) return;
    gtag('event', 'cc_option_select', {
      shape_type: shapeType,
      step: group,
      value: value
    });
  });

  // Dropdown add-ons
  var dropdownIds = [
    { id: 'cc-extra-covers', step: 'extra_covers' },
    { id: 'cc-throw-pillow', step: 'throw_pillow' },
    { id: 'cc-pillow-covers', step: 'pillow_covers' },
    { id: 'cc-armrest-pillow', step: 'armrest_pillow' },
    { id: 'cc-zipper-placement', step: 'zipper_placement' }
  ];
  dropdownIds.forEach(function (d) {
    var el = document.getElementById(d.id);
    if (!el) return;
    el.addEventListener('change', function () {
      gtag('event', 'cc_option_select', {
        shape_type: shapeType,
        step: d.step,
        value: el.value
      });
    });
  });

  /* ─── 5. cc_fabric_selected ─── */
  document.addEventListener('fabric:selected', function (e) {
    var detail = e.detail || {};
    once('cc_fabric_selected', {
      fabric_name: detail.name || detail.fabricName || '',
      fabric_price: detail.price || detail.fabricPrice || 0
    });
  });

  /* ─── 6. cc_price_shown — first time a real price (not $—) appears ─── */
  var priceEl = document.getElementById('cc-price-main');
  if (priceEl) {
    var priceObserver = new MutationObserver(function () {
      var p = getPrice();
      if (p > 0) {
        once('cc_price_shown', { price: p });
        priceObserver.disconnect();
      }
    });
    priceObserver.observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  /* ─── 7. cc_ready — add button becomes enabled ─── */
  var addBtn = document.getElementById('cc-add-btn');
  if (addBtn) {
    var btnObserver = new MutationObserver(function () {
      if (!addBtn.disabled) {
        once('cc_ready', {
          price: getPrice(),
          seconds_to_ready: secondsOnPage()
        });
        btnObserver.disconnect();
      }
    });
    btnObserver.observe(addBtn, { attributes: true, attributeFilter: ['disabled'] });
  }

  /* ─── 8. cc_add_click — add to order clicked ─── */
  document.addEventListener('configurator:add-to-cart', function (e) {
    var detail = e.detail || {};
    once('cc_add_click', {
      price: detail.price || getPrice(),
      seconds_to_add: secondsOnPage()
    });
  });

  /* ─── 9. cc_step_dwell — visible step without interaction >15s ─── */
  var DWELL_THRESHOLD = 15000;
  var sections = wrap.querySelectorAll('.cc-sec');
  var dwellTimers = {};
  var dwellFired = {};

  var stepNames = ['shape', 'fabric', 'piping', 'straps', 'zipper', 'antislip', 'addons', 'notes'];

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var idx = Array.prototype.indexOf.call(sections, entry.target);
        var stepName = stepNames[idx] || 'step_' + idx;

        if (entry.isIntersecting) {
          if (dwellFired[stepName]) return;
          dwellTimers[stepName] = setTimeout(function () {
            if (dwellFired[stepName]) return;
            dwellFired[stepName] = true;
            // Check if user has made a selection in this step
            var sec = sections[idx];
            var hasSelection = !!sec.querySelector('.cc-opt-card.sel') ||
              !!sec.querySelector('select option:checked:not([value=""]):not([value="none"]):not([value="0"])');
            gtag('event', 'cc_step_dwell', {
              shape_type: shapeType,
              step: stepName,
              dwell_seconds: Math.round(DWELL_THRESHOLD / 1000),
              has_selection: hasSelection ? 'yes' : 'no'
            });
          }, DWELL_THRESHOLD);
        } else {
          clearTimeout(dwellTimers[stepName]);
        }
      });
    }, { threshold: 0.5 });

    sections.forEach(function (sec) { io.observe(sec); });
  }

  /* ─── 10. cc_abandon — fires on page unload ─── */
  // Track which steps have been completed
  function getCompletedSteps() {
    var done = [];
    // Dimensions
    if (getElVal('cc-dim-l') && getElVal('cc-dim-w') && getElVal('cc-dim-t')) done.push('dims');
    // Card-based steps
    ['shape', 'piping', 'straps', 'zipper', 'antislip'].forEach(function (group) {
      if (wrap.querySelector('.cc-opt-card.sel[data-group="' + group + '"]')) done.push(group);
    });
    // Fabric
    if (fired.cc_fabric_selected) done.push('fabric');
    // Add-ons (any non-default)
    var addonsChanged = ['cc-extra-covers', 'cc-throw-pillow', 'cc-pillow-covers', 'cc-armrest-pillow'].some(function (id) {
      var v = getElVal(id);
      return v && v !== '0' && v !== 'none';
    });
    if (addonsChanged) done.push('addons');
    return done;
  }

  function getFunnelStage() {
    if (fired.cc_add_click) return 'added';
    if (fired.cc_ready) return 'ready';
    if (fired.cc_price_shown) return 'price_seen';
    if (fired.cc_fabric_selected) return 'fabric_done';
    if (fired.cc_dims_complete) return 'dims_done';
    if (fired.cc_dims_started) return 'dims_started';
    return 'viewed_only';
  }

  window.addEventListener('beforeunload', function () {
    var stage = getFunnelStage();
    if (stage === 'added') return; // successful add — not an abandon

    var completed = getCompletedSteps();
    gtag('event', 'cc_abandon', {
      shape_type: shapeType,
      funnel_stage: stage,
      steps_completed: completed.join(','),
      steps_done_count: completed.length,
      seconds_on_page: secondsOnPage(),
      price_seen: getPrice() || 0,
      transport_type: 'beacon'
    });
  });

  } // end boot()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
